(ns app.front.view.inspections.acknowledge
  (:require [app.domain.inspection.model :as inspection-model]
            [app.domain.party.model :as party-model]
            [app.front.entities :as entities]
            [app.front.file.download :as file-download]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.inspections.util :as insp-util]
            [app.front.view.party.common :as party-common]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]))

(def interceptors [(rf/inject-cofx :tr)])

(rf/reg-event-fx
  ::order-acknowledged
  interceptors
  (fn [{:keys [tr]} [_ status resp]]
    {:dispatch-n [[::entities/apply-changes status resp]
                  [:notifications/add :toast :success :success (tr [:order/order-acknowledged])]]}))


(rf/reg-sub ::view-sub
  :<- [::acks-pending?]
  :<- [::ws-subs/href]
  (fn [[acks-pending? ws-href]]
    {:back-href (if acks-pending?
                  (href/href :dashboard)
                  ws-href)}))

(defn public-template
  "Template function for the public html views"
  [header content]
  [:div.cloudpermit-page
   [:div.page-header
    header]
   content])

(defn- column-content [header value]
  [:div.flex-1.flex.flex-col.justify-between
   [:div.text-xs.font-bold header]
   [:div.pb-3.my-2 value]])

(defn acknowledged [tr acknowledgement]
  [:<>
   [cc/icon {:icon        i/undersign
             :fill        "green"
             :description (tr [:order/acknowledged-by])}]
   [:span.ml-2
    (tr [:order/acknowledged-by])
    " "
    [:span.font-bold
     (user-model/full-name (:order.acknowledgement/acknowledged-by acknowledgement))]
    " "
    ((<sub [::date-util/date-time-fn]) (-> acknowledgement :order.acknowledgement/acknowledged))]])


(defn download-url [signed? order-id]
  (file-download/download-href :inspection/download-order
                               {:signed signed?
                                :id     order-id}))

(defn file-list [tr order]
  (when-let [file (or (-> order :inspection.order/signed-order)
                      (-> order :inspection.order/order-document))]
    (let [signed? (some? (:inspection.order/signed-order order))]
      [:<>
       (cc/icon {:icon        i/document
                 ;; This icon seems to include about 1px white on the left side
                 :style       {:margin-left "-1px"}
                 :description (tr [:sign-off/pdf-title])})
       (cc/link {:class-name "ml-2 mr-8"
                 :href       (download-url signed? (:db/id order))
                 :target     "_blank"}
                (if signed?
                  (:file/filename file)
                  (tr [:order/download-title])))])))


(defn- row-content [tr lang acknowledged? acknowledgement order phase-target workspace id able-to-acknowledge?]
  [:div.p-4.border.border-solid.border-gray-100
   [:div.mb-6.flex.flex-col.md:flex-row.mx-2.leading-tight
    [:h4 (tr [:order/header])]]
   [:div.mb-6.flex.flex-col.md:flex-row.mx-2
    (tr [:order/content])]
   [:div.mb-6.flex.flex-col.md:flex-row.mx-2
    [:div.flex-1.md:mr-4
     [column-content
      (tr [:inspection/issued-to])
      (->> order
           :inspection.order/issued-to
           (map (fn [party]
                  (party-common/party-name-company-roles
                    (party-model/simple-name party lang)
                    (:user/corporation-or-partnership party)
                    (party-model/join-roles-as-string lang party))))
           (str/join ", "))]]
    [column-content (tr [:inspection/permit-no]) (:workspace/municipal-case-id workspace)]]
   [:div.mb-6.flex.flex-col.md:flex-row.mx-2
    [:div.flex-1.md:mr-4
     [column-content
      (tr [:inspection/compliant-by])
      ((<sub [::date-util/date-fn]) (:inspection.order/compliant-by-date order))]]
    (when (not-empty (:inspection.order/additional-address-information order))
      [:div.flex-1
       [column-content (tr [:inspection/additional-address-information]) (:inspection.order/additional-address-information order)]])]
   [:div.mb-6.flex.flex-col.md:flex-row.mx-2
    [:div.flex-1.text-left
     [file-list tr order]]
    [:div.flex-1.text-right
     (if-not acknowledged?
       [ui/modal-confirmation-button
        {:tr                  tr
         :id                  "acknowledge"
         :trigger-button-text (tr [:order/acknowledge])
         :trigger-button-opts {:kind             :primary
                               :size             :small
                               :icon             i/check
                               :icon-position    :right
                               :icon-description (tr [:order/acknowledge])
                               :disabled         (not able-to-acknowledge?)}
         :modal-title         (tr [:order/acknowledge])
         :modal-class         "text-left"
         :on-yes-click        #(>evt [:app.front.process/signal
                                      (:db/id phase-target)
                                      [:process.signal/acknowledge-order
                                       {:acknowledgement-id id}]
                                      {:on-done [::order-acknowledged]}])}
        (tr [:order/acknowledge-information])]
       [:div.bx--col-xs-12.text-right
        [acknowledged tr acknowledgement]])]]])

(rf/reg-sub ::acknowledgements
  :<- [:app.front.view.application/acknowledgements false]
  (fn [acks]
    (->> acks
         (sort-by (juxt :order.acknowledgement/acknowledged :domain/state) >))))

(rf/reg-sub ::acks-pending?
  :<- [:app.front.view.application/acknowledgements true]
  :-> boolean)

(defn acknowledge [_]
  (let [acknowledgements (<sub [::acknowledgements])
        tr               (<sub [::ws-subs/tr])
        lang             (<sub [:language])
        user             (<sub [:session/user])
        workspace        (->> (first acknowledgements)
                              :order.acknowledgement/workspace)
        props            (util/mmap tr acknowledgements)]
    (if-not workspace
      (cc/loading-spinner)
      [public-template
       ""
       [section/section
        "acknowledgements-section"
        props
        :order/acknowledgements
        #_{:clj-kondo/ignore [:deprecated-var]}
        [ui-table/responsive-table {:tr                   tr
                                    :class                "mb-4"
                                    :expandable?          true
                                    :headerless?          true
                                    :default-expanded-key (-> acknowledgements first :db/id)}
         nil
         (->> acknowledgements
              (map (fn [acknowledgement]
                     (let [id                   (:db/id acknowledgement)
                           order                (<sub [::entities/entity (-> acknowledgement :order.acknowledgement/order :db/id)])
                           phase-target         (-> order :entity/parent)
                           inspections          (->> order
                                                     :inspection.order/deficiencies
                                                     (map :entity/parent)
                                                     seq)
                           acknowledged?        (= (:domain/state acknowledgement) :order.acknowledgement.state/accepted)
                           able-to-acknowledge? (->> order
                                                     :inspection.order/issued-to
                                                     (some (partial party-model/user-in-party? user)))]
                       {:key              id
                        :opts             {:class (futil/cs "my-3 mb-4 border-2"
                                                            (when acknowledged? "bg-gray-200")
                                                            (when (not acknowledged?) "bg-gray-100"))}
                        :expanded-content [row-content
                                           tr
                                           lang
                                           acknowledged?
                                           acknowledgement
                                           order
                                           phase-target
                                           workspace
                                           id
                                           able-to-acknowledge?]
                        :cols             [{:content (str/join " - "
                                                               (remove nil?
                                                                       [(tr [:inspection/order-to-comply])
                                                                        (some->> inspections
                                                                                 (map #(inspection-model/inspection-name % lang))
                                                                                 set
                                                                                 (str/join ", "))
                                                                        (:workspace/address workspace)]))
                                            :opts    {:class "flex-wrap"}}
                                           {:opts    {:class "text-right"}
                                            :content (when acknowledged?
                                                       [insp-util/pill-text insp-util/green (tr [:order/order-acknowledged])])}]})))
              doall)]]])))

(def view
  {:name     :view/acknowledge
   ;; this is a workspace sub-page, but we actually want to show the "back to dashboard"
   ;; button as the user is not allowed to go to the workspace before acknowledging
   :view-sub [::view-sub]
   :view     acknowledge})
